import { LuFileSpreadsheet } from 'react-icons/lu';
import { pdfjs, Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { useDisclosure } from '@chakra-ui/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton } from '@chakra-ui/react';

import {
    SendDeliveryNote,
    deleteOrder,
    getOrder,
    getOrderNote,
    getOrderDeliveryNote,
    getOrderDeliveryNoteWithoutPrice,
    markOrderReady,
    resolveOrderIssue,
    updateOrder,
} from 'interfaces/order';
import Card from 'components/card';
import { getCustomers } from 'interfaces/customer';
import { getProducts } from 'interfaces/products';
import InputField from 'components/fields/InputField';
import SearchSelector from 'components/fields/SearchSelector';
import OrderTable from './components/OrderTable';
import { getTotalHT, getTotalTTC, isCustomerHT } from '../createOrders/utils';
import GoBack from 'components/actions/GoBack';
import DeleteOrder from './components/Delete';
import GenerateInvoice from './components/GenerateInvoice';
import { generateInvoiceForSpecificOrder, getNotPaidInvoicedPerCustomer } from 'interfaces/invoice';
import { FaArrowUpLong } from 'react-icons/fa6';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { productsWithDisplayKey } from '../createOrders/utils/display';
import { getDeliveryTours } from 'interfaces/deliveryTour';
import { MdEmail } from 'react-icons/md';
import GeneratePreparationButton from './components/GeneratePreparationButton';
import AddProductsModal from '../createOrders/components/AddProductModal';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const EditOrder = () => {
    const navigate = useNavigate();
    const params = useParams();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isOpenGenerateInvoice,
        onOpen: onOpenGenerateInvoice,
        onClose: onCloseGenerateInvoice,
    } = useDisclosure();
    const {
        isOpen: isOpenAddProduct,
        onOpen: onOpenAddProduct,
        onClose: onCloseAddProduct,
    } = useDisclosure();
    const [warning, setWarning] = useState<any>(null);
    const [isReady, setIsReady] = useState<boolean>(false);
    const [quantityChanged, setQuantityChanged] = useState<boolean>(false);
    const [customers, setCustomers] = useState<any>([]);
    const [products, setProducts] = useState<any>([]);
    const [deliveryTours, setDeliveryTours] = useState<any>([]);
    const [defaultDeliveryTour, setDefaultDeliveryTour] = useState<any>(true);
    const [isFullyLoaded, setIsFullyLoaded] = useState<boolean>(false);
    const [order, setOrder] = useState<any>(null);
    const [defaultCustomer, setDefaultCustomer] = useState<any>(null);
    const [newItem, setNewItem] = useState<any>({
        discountType: 'none',
        discountValue: 0,
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [pdfBlob, setPdfBlob] = useState(null);
    const [orgOrder, setOrgOrder] = useState<any>(null);
    const [pendingInvoicesSummary, setPendingInvoicesSummary] = useState<{
        count: number;
        totalLeftToPay: number;
        oldestDaysOverdue: number | null;
    } | null>(null);

    const handleChange = (e: any) => {
        setOrder((prevCustomer: any) => ({
            ...prevCustomer,
            [e.target.id]: e.target.value,
        }));
    };

    const getCustomerDeliveryTourName = () => {
        if (!order) return '';
        const customer = customers.find((c: any) => c._id === order.customerId);
        if (!customer) return 'Tournée par défaut';
        const deliveryTour = deliveryTours.find(
            (dt: any) => dt._id === customer.deliveryTourId,
        );
        return deliveryTour ? deliveryTour.name : 'Tournée par défaut';
    };

    const handleItemChange = (e: any) => {
        setNewItem((prevCustomer: any) => ({
            ...prevCustomer,
            [e.target.id]: e.target.value,
        }));
    };

    const updateQuantity = (product: any, value: number) => {
        setQuantityChanged(true);
        order.items.find((item: any) => item === product).quantity = value;
        order.totalPrice = getTotalHT(order.items);
        setOrder({
            ...order,
        });
    };

    const updateLotNumber = (product: any, value: string) => {
        order.items.find((item: any) => item === product).lotNumber = value;
        setOrder({
            ...order,
        });
    };

    const updateComment = (product: any, value: number) => {
        order.items.find((item: any) => item === product).comment = value;
        setOrder({
            ...order,
        });
    };

    const updateUnityPrice = (product: any, value: number) => {
        order.items.find((item: any) => item === product).price = value;
        order.totalPrice = getTotalHT(order.items);
        setOrder({
            ...order,
        });
    };

    const updateDiscount = (product: any, value: number) => {
        order.items.find((item: any) => item === product).discountValue = value;
        order.totalPrice = getTotalHT(order.items);
        setOrder({
            ...order,
        });
    };

    const updateDiscountType = (product: any, value: string) => {
        order.items.find((item: any) => item === product).discountType = value;
        if (value === 'none') {
            order.items.find((item: any) => item === product).discountValue = 0;
        }
        order.totalPrice = getTotalHT(order.items);
        setOrder({
            ...order,
        });
    };

    const updatePackaging = (product: any, packaging: any) => {
        const item = order.items.find((item: any) => item === product);
        if (packaging) {
            item.packagingRef = packaging.ref;
            item.packagingUnity = packaging.unity;
            item.packagingWeight = packaging.weight;
            item.price = parseFloat(packaging.price);
        } else {
            // Reset to default product values
            delete item.packagingRef;
            delete item.packagingUnity;
            delete item.packagingWeight;
            const fullProduct = products.find((p: any) => p._id === item.productId);
            if (fullProduct) {
                item.price = parseFloat(fullProduct.price);
            }
        }
        order.totalPrice = getTotalHT(order.items);
        setOrder({
            ...order,
        });
    };

    const deleteItem = (item: any) => {
        setOrder((prevCustomer: any) => ({
            ...prevCustomer,
            items: prevCustomer.items.filter(
                (i: any) => i.productId !== item.productId,
            ),
            totalPrice: getTotalHT(
                prevCustomer.items.filter(
                    (i: any) => i.productId !== item.productId,
                ),
            ),
        }));
    };

    const initOrder = async (id: string) => {
        const res = await getOrder(id);
        if (res) {
            setOrder(res);
            setOrgOrder(JSON.stringify(res));
            initProducts(res.customerId);
            fetchPendingInvoicesSummary(res.customerId);
            //setIsReady(res.isReady);
            return;
        }
        navigate('/orders');
    };

    const cleanOrder = (order: any) => {

        const newItems = order.items.map((item: any) => {
            const newItem = { ...item };
            if (newItem.price === '') newItem.price = 0;
            //delete newItem.totalPurchasePrice;
            return newItem;
        });
        return { ...order, items: newItems };
    };

    const editOrder = async () => {
        const data = { ...order };
        delete data.ownerId;
        delete data.createdAt;
        delete data.updatedAt;
        delete data.__v;
        delete data.viewed;
        const res = await updateOrder(cleanOrder(data));
        if (res.data) {
            toast.success('Modification enregistrée');
            navigate('/orders');
        }
    };
    const handleGenerateOrderNotePDF = async () => {
        try {
            const blob = await getOrderNote(params.id);
            //setPdfBlob(URL.createObjectURL(blob)); Use for testing
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `bon-commande-${order.orderId || ''}.pdf`;
            link.click();
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    const handleGeneratePDF = async () => {
        try {
            const blob = await getOrderDeliveryNote(params.id);
            //setPdfBlob(URL.createObjectURL(blob)); Use for testing
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `bon-livraison-${order.orderId || ''}.pdf`;
            link.click();
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    const handleGeneratePDFWithoutPrice = async () => {
        try {
            const blob = await getOrderDeliveryNoteWithoutPrice(params.id);
            //setPdfBlob(URL.createObjectURL(blob)); Use for testing
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `bon-livraison-${order.orderId || ''}.pdf`;
            link.click();
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    const sendDeliveryNoteByEmail = async () => {
        const res = await SendDeliveryNote(order._id);
        if (res) {
            toast.success('Bon de livraison envoyé par email');
            return;
        }
    };

    const getSelectedCustomer = () => {
        if (order && customers) {
            const customer = customers.find(
                (c: any) => c._id === order.customerId,
            );
            if (customer) {
                return customer;
            }
        }
        return null;
    };

    const isSelectedCustomerHT = () => isCustomerHT(getSelectedCustomer());

    const initCustomers = async () => {
        const res = await getCustomers();
        if (res) setCustomers(res.data);
    };

    const initProducts = async (customerId: string) => {
        const res = await getProducts(1, 10000, customerId);
        if (res) {
            const productsForSales = res.data;
            setProducts(productsForSales);
            setIsFullyLoaded(true);
        }
    };

    const initDeliveryTours = async () => {
        const res = await getDeliveryTours(false);
        if (res) {
            setDeliveryTours(res.data);
            return;
        }
    };

    // Add totatPurchasePrice to each item
    useEffect(() => {
        if (!products || !order) return;
        const updatedOrder = { ...order };
        setOrder(updatedOrder);
    }, [products, isFullyLoaded]);

    useEffect(() => {
        if (localStorage.getItem('token') === null) {
            navigate('/login');
        }
        initOrder(params.id);
        initCustomers();
        initDeliveryTours();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateProductsPrices = async (products: any) => {
        const newItemsUpdated = products.find(
            (product: any) => product._id === newItem._id,
        );
        let isNewOrderUpdated = false;
        const newOrdersItemsUpdated = order.items.map((item: any) => {
            const itemUpdated = products.find(
                (product: any) => product._id === item._id,
            );
            if (itemUpdated) {
                item.price = itemUpdated.price;
                isNewOrderUpdated = true;
            }
            return item;
        });

        const customer = customers.find(
            (customer: any) => customer._id === order.customerId,
        );
        const warning = `Prix unitaire HT modifié en fonction du prix négocié avec ${
            customer ? customer.company : 'le client'
        }.`;
        if (newItemsUpdated !== undefined) {
            setWarning(warning);
            setNewItem((prevCustomer: any) => ({
                ...prevCustomer,
                price: newItemsUpdated.price,
            }));
        }

        if (isNewOrderUpdated) {
            setWarning(warning);
            setOrder((prevCustomer: any) => ({
                ...prevCustomer,
                items: newOrdersItemsUpdated,
                totalPrice: getTotalHT(newOrdersItemsUpdated),
            }));
        }
    };

    const fetchPendingInvoicesSummary = async (customerId: string) => {
        if (!customerId) {
            setPendingInvoicesSummary(null);
            return;
        }
        const res = await getNotPaidInvoicedPerCustomer(1000, 1, customerId);
        if (!res || res.error || !res.data?.data) {
            setPendingInvoicesSummary(null);
            return;
        }
        const invoices = res.data.data;
        const count = invoices.length;
        const totalLeftToPay = invoices.reduce((acc: number, inv: any) => {
            return acc + Math.max(0, Number(inv.leftToPay) || 0);
        }, 0);
        let oldestDate: Date | null = null;
        for (const inv of invoices) {
            const refDate = inv.dueDate || inv.invoicedAt || inv.createdAt;
            if (refDate) {
                const d = new Date(refDate);
                if (!oldestDate || d < oldestDate) oldestDate = d;
            }
        }
        const today = new Date();
        const oldestDaysOverdue =
            oldestDate !== null
                ? Math.floor(
                      (today.getTime() - oldestDate.getTime()) /
                          (1000 * 60 * 60 * 24),
                  )
                : null;
        setPendingInvoicesSummary({ count, totalLeftToPay, oldestDaysOverdue });
    };

    const fetchProductsPrices = async (customerId: string) => {
        setWarning('Chargement des prix personnalisés en cours...');
        const res = await getProducts(1, 10000, customerId);
        if (res) {
            // const productsForSales = res.data.filter(
            //     (product: any) => product.category !== 'hidden',
            // );
            setProducts(res.data);
            updateProductsPrices(res.data);
        }
    };

    const getStatusList = (order: any) => {
        let status = [
            {
                status: 'Brouillon',
                _id: 'draft',
            },
            {
                status: 'En cours',
                _id: 'pending',
            },
            {
                status: 'Livré',
                _id: 'delivered',
            },
            {
                status: 'Annulé',
                _id: 'canceled',
            },
            {
                status: 'Déjà facturé',
                _id: 'already-invoiced',
            },
        ];
        if (order && order.invoiced === true) {
            status = status.filter((s: any) => s._id !== 'canceled');
        }
        return status;
    };

    useEffect(() => {
        if (!defaultCustomer) {
            setDefaultCustomer(getSelectedCustomer());
            return;
        }
        if (order && order.customerId) {
            fetchProductsPrices(order.customerId);
            fetchPendingInvoicesSummary(order.customerId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order?.customerId]);

    const alreadyExist = (product: any) => {
        return order.items.find(
            (product: any) => product.productId === newItem.productId,
        );
    };

    const resolveIssue = async () => {
        const res = await resolveOrderIssue(order._id);
        if (res) {
            setOrder((prevCustomer: any) => ({
                ...prevCustomer,
                issueToResolve: false,
            }));
        }
    };

    const isValidOrder = (order: any) => {
        return (
            orgOrder &&
            order.orderId &&
            order.status &&
            order.items.length > 0 &&
            order.deliveryDate &&
            order.customerId &&
            orgOrder !== JSON.stringify(order) &&
            order.totalPrice >= 0
        );
    };

    const totalBrutWeight = () => {
        return order.items
            .reduce(
                (acc: number, item: any) =>
                    acc +
                    (isNaN(Number(item.brutWeight * item.quantity))
                        ? 0
                        : Number(item.brutWeight * item.quantity)),
                0,
            )
            ?.toFixed(2);
    };

    const totalNetWeight = () => {
        return order.items
            .reduce(
                (acc: number, item: any) =>
                    acc +
                    (isNaN(Number(item.netWeight * item.quantity))
                        ? 0
                        : Number(item.netWeight * item.quantity)),
                0,
            )
            ?.toFixed(2);
    };

    const generateInvoice = async () => {
        await editOrder();
        const res = await generateInvoiceForSpecificOrder(order._id);

        if (res.data) {
            toast.success('Facture générée avec succès');
            window.open(res.data.url, '_blank');
            setOrder((prevOrder: any) => ({
                ...prevOrder,
                invoiced: true,
            }));
        }
        onCloseGenerateInvoice();
    };

    const handleExportXLSX = () => {
        const customer = getSelectedCustomer();
        if (!customer || !order) return;

        // Préparer les données pour chaque produit de la commande
        const exportData = order.items.map((item: any) => {
            const product = products.find((p: any) => p._id === item.productId);
            return {
                'Reference': order.orderId || '',
                'Product - Sku': product?.ref || item.ref || '',
                'Product - Lot': item.lotNumber || '',
                'Product - Qty': item.quantity || '',
                'Product - Unit Price HT': '',
                'Product - Position Type': '',
                'Carrier code': 'VOID',
                'Relay Id': '',
                'Insurance': '',
                'Currency': 'EUR',
                'Comment': '',
                'Recipient - Company': customer.displayName || customer.company || '',
                'Recipient - Email': customer.mail || '',
                'Recipient - First name': customer.displayName || customer.company || '',
                'Recipient - Last name': customer.displayName || customer.company || '',
                'Recipient - Address 1': customer.deliveryAddress || '',
                'Recipient - Address 2': '',
                'Recipient - Address 3': '',
                'Recipient - Address 4': '',
                'Recipient - Phone Number': '0000000000',
                'Recipient - City': '',
                'Recipient - Zip Code': '',
                'Recipient - Country': 'FR',
                'Recipient - State': '',
                'Process Type': ''
            };
        });

        // Créer le fichier XLSX
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Commande');
        
        // Télécharger le fichier
        XLSX.writeFile(workbook, `commande-${order.orderId || order._id}.xlsx`);
        toast.success('Fichier XLSX téléchargé avec succès');
    };

    const handlePrintTrackingLabel = () => {
        const customer = getSelectedCustomer();
        const deliveryTour = deliveryTours.find(
            (dt: any) => dt._id === (order?.deliveryTourId || customer?.deliveryTourId)
        );
        
        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <title>Étiquette de suivi - ${order.orderId}</title>
                <style>
                    @page {
                        size: 50mm 60mm;
                        margin: 2mm;
                    }
                    body {
                        font-family: Arial, sans-serif;
                        padding: 0;
                        margin: 0;
                        width: 50mm;
                        height: 60mm;
                    }
                    .label-container {
                        border: 1px solid #000;
                        padding: 3mm;
                        width: 100%;
                        height: 100%;
                        box-sizing: border-box;
                    }
                    .label-row {
                        margin-bottom: 2mm;
                        border-bottom: 1px solid #ccc;
                        padding-bottom: 1mm;
                    }
                    .label-row:last-child {
                        border-bottom: none;
                        margin-bottom: 0;
                    }
                    .label-title {
                        font-weight: bold;
                        font-size: 7px;
                        color: #666;
                        margin-bottom: 1mm;
                    }
                    .label-value {
                        font-size: 10px;
                        font-weight: bold;
                        color: #000;
                    }
                    .company-name {
                        font-size: 11px;
                        font-weight: bold;
                        color: #0260CB;
                    }
                    .address {
                        font-size: 8px;
                        line-height: 1.2;
                    }
                </style>
            </head>
            <body>
                <div class="label-container">
                    <div class="label-row">
                        <div class="label-title">N°BL</div>
                        <div class="label-value">${order.orderId || 'N/A'}</div>
                    </div>
                    <div class="label-row">
                        <div class="label-title">N° de la tournée</div>
                        <div class="label-value">${deliveryTour?.name || 'Tournée par défaut'}</div>
                    </div>
                    <div class="label-row">
                        <div class="label-title">Client</div>
                        <div class="company-name">${customer?.company || 'N/A'}</div>
                    </div>
                    <div class="label-row">
                        <div class="label-title">Adresse de livraison</div>
                        <div class="address">
                            ${customer?.deliveryAddress || 'Adresse non renseignée'}
                        </div>
                    </div>
                </div>
                <script>
                    window.onload = function() {
                        window.print();
                    };
                </script>
            </body>
            </html>
        `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();
    };

    return (
        <div className="px-2 sm:px-4 lg:px-0">
            <GoBack />
            <div className="mt-1 grid grid-cols-1 lg:grid-cols-12 gap-2 sm:gap-3">
                <DeleteOrder
                    isOpen={isOpen}
                    onClose={onClose}
                    description="Êtes-vous sûr de vouloir supprimer cette commande ?"
                    context="d'une commande"
                    archived={false}
                    deleteFunction={() => {
                        deleteOrder(params.id);
                        navigate('/orders');
                    }}
                />
                <GenerateInvoice
                    isOpen={isOpenGenerateInvoice}
                    onClose={onCloseGenerateInvoice}
                    onClick={generateInvoice}
                />
                <AddProductsModal
                    isOpen={isOpenAddProduct}
                    onClose={onCloseAddProduct}
                    addProducts={(newItems: any) => {
                        setOrder((prev: any) => ({
                            ...prev,
                            items: [...prev.items, ...newItems],
                            totalPrice: getTotalHT([...prev.items, ...newItems]),
                        }));
                        onCloseAddProduct();
                    }}
                    existingProducts={order ? order.items.map((item: any) => item._id) : []}
                    customerId={order?.customerId}
                />
                <Card extra={'col-span-1 lg:col-span-8 w-full !p-3 sm:!p-5 border'}>
                    {/* Header */}
                    <div className="h-full w-full">
                        <div className="relative">
                            <div className="flex items-center gap-3">
                                <h4 className="text-base sm:text-lg font-medium text-navy-700 dark:text-white">
                                    Commande n°{order ? order.orderId : ''}
                                </h4>
                                <button
                                    className="linear cursor-pointer items-center justify-center rounded-lg border border-green-600 px-2 py-1 text-xs text-green-600 transition duration-200 hover:bg-green-50 active:bg-green-100 whitespace-nowrap"
                                    onClick={handleExportXLSX}
                                >
                                    Export XLSX
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-1 sm:gap-2 mt-3">
                                <button
                                    className="hidden sm:flex linear cursor-pointer items-center justify-center rounded-xl border border-[#01499b] px-2 py-1 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80"
                                    onClick={handleGenerateOrderNotePDF}
                                >
                                    Générer un bon de commande
                                </button>
                                <button
                                    className="hidden sm:flex linear cursor-pointer items-center justify-center rounded-xl border border-[#01499b] px-2 py-1 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80"
                                    onClick={handleGeneratePDFWithoutPrice}
                                >
                                    Générer BL non valorisé
                                </button>
                                <button
                                    className="hidden sm:flex linear cursor-pointer items-center justify-center rounded-xl border border-[#01499b] px-2 py-1 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80"
                                    onClick={handleGeneratePDF}
                                >
                                    Générer un bon de livraison
                                </button>
                                <button
                                    className="hidden sm:flex linear cursor-pointer items-center gap-2  justify-center rounded-xl border border-[#01499b] px-2 py-1 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80"
                                    onClick={sendDeliveryNoteByEmail}
                                >
                                    <MdEmail size={13} />
                                    Envoyer le BL
                                </button>
                                {order && !order.isReady && !order.invoiced && order.status !== 'already-invoiced' && (
                                    <GeneratePreparationButton
                                        orderId={order._id}
                                        orderStatus={order.status}
                                        isReady={order.isReady}
                                    />
                                )}
                                {order && !order.invoiced && order.status !== 'already-invoiced' && (
                                    <button
                                        className="hidden sm:flex linear cursor-pointer items-center justify-center rounded-xl border border-[#01499b] px-2 py-1 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80"
                                        onClick={onOpenGenerateInvoice}
                                    >
                                        Facturer la commande
                                    </button>
                                )}
                                <button
                                    className="flex sm:hidden w-[50px] h-[50px] linear cursor-pointer items-center justify-center rounded-full px-2 py-2 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80"
                                    onClick={handleGeneratePDF}
                                >
                                    <LuFileSpreadsheet size={30} />
                                </button>
                            </div>
                            <div className="h-full w-full">
                                {/* <p className="mt-1 text-base text-gray-600">
                                    {order && order.invoiced
                                        ? 'Commande déjà facturée, disponible en lecture-seule.'
                                        : ''}
                                </p> */}
                                {pdfBlob && (
                                    <div className="">
                                        <Document
                                            className="flex w-full border rounded-3xl bg-gray-400"
                                            file={pdfBlob}
                                        >
                                            <Page pageNumber={1} />
                                        </Document>
                                    </div>
                                )}
                                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3">
                                    <div className="col-span-1 sm:col-span-1 lg:col-span-3">
                                        <InputField
                                            label="Date de livraison*"
                                            placeholder="01/01/2021"
                                            id="deliveryDate"
                                            type="date"
                                            value={
                                                order && order.deliveryDate
                                                    ? order.deliveryDate.split(
                                                          'T',
                                                      )[0]
                                                    : ''
                                            }
                                            onChange={handleChange}
                                            disabled={
                                                order &&
                                                order.invoiced === false
                                                    ? false
                                                    : true
                                            }
                                            inputSmall={true}
                                        />
                                    </div>
                                    <div className="col-span-1 sm:col-span-1 lg:col-span-3">
                                        <SearchSelector
                                            label="Statut"
                                            options={getStatusList(order)}
                                            displayKey="status"
                                            value={order ? order.status : ''}
                                            onSelect={(e: any) => {
                                                setOrder(
                                                    (prevCustomer: any) => ({
                                                        ...prevCustomer,
                                                        status: e._id,
                                                    }),
                                                );
                                            }}
                                            inputSmall={true}
                                        />
                                        {order &&
                                            order.status === 'canceled' && (
                                                <p className="mt-1  ml-3 text-xs text-gray-400">
                                                    💡 Pour garantir une
                                                    traçabilitée parfaite pensez
                                                    à retirer le contenu de la
                                                    commande de votre stock
                                                    après annulation.
                                                </p>
                                            )}
                                        {order &&
                                            order.status === 'already-invoiced' && (
                                                <p className="mt-1  ml-3 text-xs text-gray-400">
                                                    💡 Cette commande ne sera pas facturable 
                                                    car elle est marquée comme déjà facturée.
                                                </p>
                                            )}
                                    </div>
                                    <div className="col-span-1 sm:col-span-1 lg:col-span-3">
                                        <SearchSelector
                                            label="Client*"
                                            displayKey="company"
                                            options={customers}
                                            value={
                                                order ? order.customerId : ''
                                            }
                                            disabled={
                                                order &&
                                                order.invoiced === false
                                                    ? false
                                                    : true
                                            }
                                            onSelect={(e: any) => {
                                                setOrder(
                                                    (prevCustomer: any) => ({
                                                        ...prevCustomer,
                                                        customerId: e._id,
                                                    }),
                                                );
                                            }}
                                            inputSmall={true}
                                        />
                                    </div>
                                    <div className="col-span-1 sm:col-span-1 lg:col-span-3">
                                        <InputField
                                            label="Numéro purchase order (PO)"
                                            placeholder="PO-7578"
                                            id="purchaseOrderNumber"
                                            type="text"
                                            value={
                                                order
                                                    ? order.purchaseOrderNumber
                                                    : ''
                                            }
                                            onChange={handleChange}
                                            inputSmall={true}
                                        />
                                    </div>
                                </div>
                                <div className="mt-2 grid grid-cols-1 lg:grid-cols-12 gap-3">
                                    <div className="col-span-1 lg:col-span-6">
                                        <InputField
                                            label="Note du client"
                                            placeholder={`Pouvez-vous livrer avant 8h ?`}
                                            id="customerNotes"
                                            type="text"
                                            value={
                                                order ? order.customerNotes : ''
                                            }
                                            disabled={
                                                order &&
                                                order.invoiced === false
                                                    ? false
                                                    : true
                                            }
                                            onChange={handleChange}
                                            inputSmall={true}
                                        />
                                    </div>
                                    <div className="col-span-1 lg:col-span-3">
                                        {defaultDeliveryTour &&
                                        !order?.deliveryTourId ? (
                                            <>
                                                <label
                                                    htmlFor={
                                                        'deliverytourlabel'
                                                    }
                                                    className={`text-xs font-medium text-navy-700 dark:text-white`}
                                                >
                                                    Tournée de livraison
                                                </label>
                                                <p className="text-sm">
                                                    {getCustomerDeliveryTourName()}
                                                </p>
                                                <p
                                                    className="text-xs text-toola-500 cursor-pointer hover:underline"
                                                    onClick={() =>
                                                        setDefaultDeliveryTour(
                                                            false,
                                                        )
                                                    }
                                                >
                                                    Modifier sur cette commande
                                                </p>
                                            </>
                                        ) : (
                                            <SearchSelector
                                                label="Tournée de livraison"
                                                displayKey="name"
                                                options={deliveryTours}
                                                value={order?.deliveryTourId}
                                                onSelect={(e: any) => {
                                                    setOrder((prev: any) => ({
                                                        ...prev,
                                                        deliveryTourId: e._id,
                                                    }));
                                                }}
                                                canCreate={true}
                                                createNew={() =>
                                                    window.open(
                                                        '/settings/deliveries',
                                                        '_blank',
                                                    )
                                                }
                                                inputSmall={true}
                                            />
                                        )}
                                    </div>
                                    <div className="col-span-1 lg:col-span-3">
                                        <InputField
                                            label={`Note interne`}
                                            placeholder="Livraison prioritaire"
                                            id="internalNotes"
                                            type="text"
                                            value={
                                                order ? order.internalNotes : ''
                                            }
                                            disabled={
                                                order &&
                                                order.invoiced === false
                                                    ? false
                                                    : true
                                            }
                                            onChange={handleChange}
                                            inputSmall={true}
                                        />
                                    </div>
                                    <div className="col-span-1 lg:col-span-12">
                                        <InputField
                                            label={`Note de livraison (figure sur le BL)`}
                                            placeholder="Livraison prioritaire"
                                            id="deliveryNotes"
                                            type="text"
                                            value={
                                                order ? order.deliveryNotes : ''
                                            }
                                            disabled={
                                                order &&
                                                order.invoiced === false
                                                    ? false
                                                    : true
                                            }
                                            onChange={handleChange}
                                            inputSmall={true}
                                        />
                                    </div>
                                </div>
                                {order && !isReady && (
                                    <div className="flex flex-col sm:flex-row gap-2 mt-4">
                                        <button
                                            className="border border-toola-400 text-toola-400 text-xs px-3 py-1.5 rounded-lg hover:bg-toola-50 whitespace-nowrap flex-shrink-0"
                                            onClick={async (e: any) => {
                                                e.stopPropagation();
                                                await markOrderReady(
                                                    order?._id,
                                                );
                                                setIsReady(true);
                                            }}
                                        >
                                            Terminer la préparation
                                        </button>
                                        <button
                                            className="border border-blue-600 text-blue-600 text-xs px-3 py-1.5 rounded-lg hover:bg-blue-50 whitespace-nowrap flex-shrink-0"
                                            onClick={(e: any) => {
                                                e.stopPropagation();
                                                handlePrintTrackingLabel();
                                            }}
                                        >
                                            Imprimer étiquette de suivi
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {order && order.issueToResolve && (
                        <div className="mt-3 p-4 rounded-xl shadow-lg border">
                            <p className="text-red-500 text-sm">
                                Un problème ou un commentaire de livraison est
                                ouvert pour cette commande (voir note de
                                livraison).
                            </p>
                            <p
                                className="mt-2 text-sm p-2 text-center cursor-pointer rounded-xl bg-red-300 border"
                                onClick={resolveIssue}
                            >
                                Marquer comme résolu
                            </p>
                        </div>
                    )}
                </Card>
                <Card extra={'w-full !p-3 sm:!p-5 border col-span-1 lg:col-span-4'}>
                    {/* Header */}
                    <div className="h-full w-full">
                        <div className="relative">
                            <div className="flex gap-[10px] absolute right-0 top-0"></div>
                            <div className="h-full w-full">
                                <div className="flex justify-between items-start">
                                    <h4 className="text-base sm:text-lg font-medium text-navy-700 dark:text-white">
                                        Client
                                    </h4>
                                    <div className="">
                                        <button
                                            className="linear flex cursor-pointer items-center justify-center rounded-xl border border-red-500 px-2 py-1 sm:py-2 text-[10px] sm:text-xs text-red-500 transition duration-200 hover:bg-red-50 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80 whitespace-nowrap"
                                            onClick={() => onOpen()}
                                        >
                                            Supprimer la commande
                                        </button>
                                    </div>
                                </div>
                                <p className="mt-2 text-xs sm:text-sm text-gray-600 break-words">
                                    {getSelectedCustomer() &&
                                        `Nom: ${
                                            getSelectedCustomer().company
                                        }`}{' '}
                                </p>
                                <p className="mt-2 text-xs sm:text-sm text-gray-600 break-words">
                                    {getSelectedCustomer() &&
                                        `Email: ${
                                            getSelectedCustomer().mail
                                        }`}{' '}
                                </p>
                                <p className="mt-2 text-xs sm:text-sm text-gray-600 break-words">
                                    {getSelectedCustomer() &&
                                        `Téléphone: ${
                                            getSelectedCustomer().phone ||
                                            'Non renseigné'
                                        }`}
                                    {!getSelectedCustomer() &&
                                        'Aucune information client'}
                                </p>
                                {pendingInvoicesSummary &&
                                    pendingInvoicesSummary.count > 0 && (
                                        <div className="mt-3 p-2 sm:p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                            <p className="text-xs font-semibold text-orange-700">
                                                ⚠️{' '}
                                                {pendingInvoicesSummary.count}{' '}
                                                facture
                                                {pendingInvoicesSummary.count > 1
                                                    ? 's'
                                                    : ''}{' '}
                                                en attente
                                            </p>
                                            <p className="text-xs text-gray-600 mt-1">
                                                À régler :{' '}
                                                <span className="font-bold text-orange-600">
                                                    {pendingInvoicesSummary.totalLeftToPay.toFixed(2)}€
                                                </span>
                                            </p>
                                            {pendingInvoicesSummary.oldestDaysOverdue !== null && (
                                                <p className="text-xs text-gray-600 mt-0.5">
                                                    Retard (+ ancienne) :{' '}
                                                    <span
                                                        className={`font-bold ${
                                                            pendingInvoicesSummary.oldestDaysOverdue > 30
                                                                ? 'text-red-600'
                                                                : 'text-orange-600'
                                                        }`}
                                                    >
                                                        {pendingInvoicesSummary.oldestDaysOverdue}{' '}
                                                        jour
                                                        {pendingInvoicesSummary.oldestDaysOverdue > 1
                                                            ? 's'
                                                            : ''}
                                                    </span>
                                                </p>
                                            )}
                                        </div>
                                    )}
                                <p
                                    className="flex gap-2 mt-2 text-xs sm:text-sm text-blue-500 hover:cursor-pointer break-words"
                                    onClick={() =>
                                        window.open(
                                            `/customer/${order.customerId}`,
                                            '_blank',
                                        )
                                    }
                                >
                                    <FaExternalLinkAlt
                                        size={12}
                                        className="flex my-auto"
                                    />
                                    Voir la fiche client
                                </p>
                            </div>
                        </div>
                    </div>
                </Card>
                <Card extra={'col-span-1 lg:col-span-12 w-full !p-3 sm:!p-5 border'}>
                    {/* Header */}
                    <div className="h-full w-full">
                        <div>
                            <h4 className="text-base sm:text-lg font-medium text-navy-700 dark:text-white">
                                Contenu de la commande
                            </h4>
                            {order && !order.invoiced && (
                                <>
                                    {warning && (
                                        <p className="mt-1 text-xs sm:text-sm text-red-500">
                                            {warning}
                                        </p>
                                    )}
                                    <div className="mt-3 flex items-center gap-3">
                                        <button
                                            className="linear flex items-center gap-1 justify-center rounded-xl bg-toola-500 hover:bg-toola-600 active:bg-toola-700 px-3 py-1.5 text-xs font-medium text-white whitespace-nowrap"
                                            onClick={onOpenAddProduct}
                                        >
                                            Parcourir les produits
                                        </button>
                                    </div>
                                </>
                            )}
                            <p className="mt-4 text-sm sm:text-base text-gray-600">
                                Récapitulatif de la commande
                            </p>
                            {order && (
                                <OrderTable
                                    order={order}
                                    products={products}
                                    handleQtyChange={updateQuantity}
                                    handleLotNumberChange={updateLotNumber}
                                    handlePriceChange={updateUnityPrice}
                                    handleDiscountChange={updateDiscount}
                                    handleDiscountTypeChange={
                                        updateDiscountType
                                    }
                                    handleCommentChange={updateComment}
                                    handlePackagingChange={updatePackaging}
                                    onDelete={deleteItem}
                                    customerId={order?.customerId}
                                />
                            )}
                        </div>
                        {quantityChanged && (
                            <p className="mt-4 text-sm text-red-500">
                                <p className="mt-1  ml-3 text-sm text-gray-400">
                                    💡 Pour garantir une traçabilitée parfaite
                                    pensez à réajuster votre stock après édition
                                    de la commande.
                                </p>
                            </p>
                        )}
                        {order && order.items && order.items.length > 4 && (
                            <div className="mt-2 flex gap-2">
                                <p
                                    className="flex my-auto text-toola-500 hover:cursor-pointer hover:text-toola-600"
                                    onClick={() => {
                                        window.scrollTo({
                                            top: 0,
                                            behavior: 'auto',
                                        }); // behaviors: smooth, auto, instant
                                    }}
                                >
                                    Ajouter un produit
                                </p>
                                <FaArrowUpLong
                                    size={10}
                                    className="flex my-auto text-toola-500"
                                />
                            </div>
                        )}
                        {order && order.items && order.items.length > 0 && (
                            <div className="flex flex-wrap gap-3 sm:gap-6 lg:gap-10">
                                <div className="mt-3 sm:mt-5 flex items-center gap-2 min-w-0">
                                    <p className="text-sm sm:text-base lg:text-lg text-navy-700 dark:text-white whitespace-nowrap">
                                        Total HT:
                                    </p>
                                    <p className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                                    {getTotalHT(order.items).toFixed(2)}€
                                    </p>
                                </div>
                                {getTotalTTC(order.items, {
                                    isCustomerHT: isSelectedCustomerHT(),
                                }) > 0 && (
                                    <div className="mt-3 sm:mt-5 flex items-center gap-2 min-w-0">
                                        <p className="text-sm sm:text-base lg:text-lg text-navy-700 dark:text-white whitespace-nowrap">
                                            Total TTC:
                                        </p>
                                        <p className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                                        {getTotalTTC(order.items, {
                                            isCustomerHT: isSelectedCustomerHT(),
                                        }).toFixed(2)}€
                                        </p>
                                    </div>
                                )}
                                {totalNetWeight() > 0 && (
                                    <div className="mt-3 sm:mt-5 flex items-center gap-2 min-w-0">
                                        <p className="text-sm sm:text-base lg:text-lg text-navy-700 dark:text-white whitespace-nowrap">
                                            Poids net:
                                        </p>
                                        <p className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                                            {totalNetWeight()}
                                            Kg
                                        </p>
                                    </div>
                                )}
                                {totalBrutWeight() > 0 && (
                                    <div className="mt-3 sm:mt-5 flex items-center gap-2 min-w-0">
                                        <p className="text-sm sm:text-base lg:text-lg text-navy-700 dark:text-white whitespace-nowrap">
                                            Poids brut:
                                        </p>
                                        <p className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                                            {totalBrutWeight()}
                                            Kg
                                        </p>
                                    </div>
                                )}
                                {order.numberOfArticles > 0 && (
                                    <div className="mt-3 sm:mt-5 flex items-center gap-2 min-w-0">
                                        <p className="text-sm sm:text-base lg:text-lg text-navy-700 dark:text-white whitespace-nowrap">
                                            Nb Art.:
                                        </p>
                                        <p className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                                            {order.numberOfArticles}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="col-span-1">
                            <InputField
                                label="Nombre de palettes"
                                placeholder="0"
                                id="pcbTotal"
                                type="number"
                                value={order?.pcbTotal ?? ''}
                                disabled={order && order.invoiced === false ? false : true}
                                onChange={handleChange}
                                inputSmall={true}
                            />
                        </div>
                        <div className="col-span-1">
                            <InputField
                                label="Préparé par"
                                placeholder="Lucas"
                                id="preparedBy"
                                type="string"
                                value={order?.preparedBy ?? ''}
                                disabled={order && order.invoiced === false ? false : true}
                                onChange={handleChange}
                                inputSmall={true}
                            />
                        </div>
                    </div>
                </Card>
                {order && (
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-5 px-2 sm:px-0">
                        <button
                            className={`linear flex items-center justify-center rounded-lg px-5 py-2.5 text-sm font-medium min-w-[140px] ${
                                isValidOrder(order)
                                    ? 'bg-toola-500 hover:bg-toola-600 active:bg-toola-700 text-white'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                            onClick={() => {
                                isValidOrder(order) && editOrder();
                            }}
                        >
                            Sauvegarder
                        </button>
                        <button
                            className="linear border border-toola-500 text-toola-500 hover:bg-toola-50 active:bg-toola-100 flex items-center justify-center rounded-lg px-5 py-2.5 text-sm font-medium whitespace-nowrap min-w-[140px]"
                            onClick={() => {
                                navigate(`/create-order`);
                            }}
                        >
                            + nouvelle commande
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EditOrder;
